import { useEffect, useState } from 'react'
import { X, Plus, Trash2, Save, AlertCircle } from 'lucide-react'
import type { SavedTemplate } from '@/hooks/useRosterTemplates'

interface TemplateEditDialogProps {
  template: SavedTemplate | null
  onClose: () => void
  onSave: (
    id: string,
    changes: { name: string; cycles: Array<{ daysOn: number; daysOff: number }> }
  ) => Promise<void>
}

export default function TemplateEditDialog({ template, onClose, onSave }: TemplateEditDialogProps) {
  const [name, setName] = useState('')
  const [cycles, setCycles] = useState<Array<{ daysOn: number; daysOff: number }>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (template) {
      setName(template.name)
      setCycles(template.cycles.map((c) => ({ daysOn: c.daysOn, daysOff: c.daysOff })))
      setError('')
      setSaving(false)
    }
  }, [template])

  useEffect(() => {
    if (!template) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [template, onClose])

  if (!template) return null

  const updateCycle = (index: number, field: 'daysOn' | 'daysOff', value: number) => {
    setCycles((prev) =>
      prev.map((cycle, i) => (i === index ? { ...cycle, [field]: value } : cycle))
    )
  }

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Give your template a name.')
      return
    }
    if (cycles.some((c) => !Number.isFinite(c.daysOn) || c.daysOn < 1 || !Number.isFinite(c.daysOff) || c.daysOff < 1)) {
      setError('Every cycle needs at least 1 work day and 1 rest day.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave(template._id, { name: trimmed, cycles })
      onClose()
    } catch (err) {
      console.error('Failed to update template:', err)
      setError('Could not save your changes. Please try again.')
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-edit-title"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 id="template-edit-title" className="text-xl font-bold text-slate-900">
            Edit Template
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
            aria-label="Close edit template dialog"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="template-name">
          Template name
        </label>
        <input
          id="template-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-2.5 mb-5 text-sm border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          placeholder="e.g. FIFO nights 8-6-7-7"
        />

        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Cycles</span>
          <button
            onClick={() => setCycles((prev) => [...prev, { daysOn: 4, daysOff: 4 }])}
            className="flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900"
          >
            <Plus className="w-3.5 h-3.5" />
            Add cycle
          </button>
        </div>

        <div className="space-y-2 mb-5">
          {cycles.map((cycle, index) => (
            <div key={index} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg">
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1" htmlFor={`on-${index}`}>
                  Work days
                </label>
                <input
                  id={`on-${index}`}
                  type="number"
                  min={1}
                  max={60}
                  value={cycle.daysOn}
                  onChange={(e) => updateCycle(index, 'daysOn', Number(e.target.value))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1" htmlFor={`off-${index}`}>
                  Rest days
                </label>
                <input
                  id={`off-${index}`}
                  type="number"
                  min={1}
                  max={60}
                  value={cycle.daysOff}
                  onChange={(e) => updateCycle(index, 'daysOff', Number(e.target.value))}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <button
                onClick={() => setCycles((prev) => prev.filter((_, i) => i !== index))}
                disabled={cycles.length === 1}
                className="p-1.5 mt-4 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent"
                aria-label={`Remove cycle ${index + 1}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
